export interface Chain {
  slug: string;
  name: string;
  description: string;
  /** Full SEO title — used as absolute <title> (bypasses layout template). */
  seoTitle?: string;
  /** Meta description (<160 chars). */
  seoDescription?: string;
  /** Logo path, or empty string when no logo is available (renders letter fallback). */
  logoSrc: string;
  category: "L1" | "L2" | "EVM" | "non-EVM";
  /** Whether a dedicated /chains/{slug} page exists. False entries render as non-clickable cards. */
  hasPage: boolean;
  /** Lower = higher priority. Used to pick the top-10 for the header dropdown. */
  priority?: number;
}

export const chains: Chain[] = [
  /* ── Existing 15 (have dedicated pages) ─────────────────────────────────── */
  {
    slug: "ethereum",
    name: "Ethereum",
    description: "The leading smart contract platform. Run full archive and pruned Ethereum RPC nodes.",
    seoTitle: "Ethereum RPC Endpoints | Dedicated Ethereum Nodes | Instanodes",
    seoDescription:
      "Enterprise-grade Ethereum RPC nodes. Low latency, 99.95% uptime, archive access, dedicated and shared options. Build on Ethereum with Instanodes.",
    logoSrc: "/images/chains/ethereum.svg",
    category: "L1",
    hasPage: true,
    priority: 1,
  },
  {
    slug: "bitcoin-nodes",
    name: "Bitcoin",
    description: "The original blockchain. Full node and indexer support for Bitcoin infrastructure.",
    seoTitle: "Bitcoin Nodes | Full Bitcoin RPC Endpoints and Indexers | Instanodes",
    seoDescription:
      "Run dedicated Bitcoin full nodes and indexers. Reliable RPC access, high availability, and managed infrastructure from Instanodes.",
    logoSrc: "/images/chains/bitcoin.svg",
    category: "non-EVM",
    hasPage: true,
    priority: 2,
  },
  {
    slug: "solana",
    name: "Solana",
    description: "High-throughput blockchain with low fees. Optimized RPC Endpoints for DeFi and NFTs.",
    seoTitle: "Solana RPC Endpoints | Dedicated Solana Nodes | Instanodes",
    seoDescription:
      "High-performance Solana RPC nodes. Optimized for DeFi, NFT, and wallet applications. Dedicated endpoints with global distribution by Instanodes.",
    logoSrc: "/images/chains/solana.svg",
    category: "non-EVM",
    hasPage: true,
    priority: 3,
  },
  {
    slug: "bsc-nodes",
    name: "BNB Smart Chain",
    description: "High-performance EVM-compatible chain from Binance. Ideal for DeFi protocols.",
    seoTitle: "BSC Nodes | BNB Smart Chain RPC Endpoints | Instanodes",
    seoDescription:
      "BNB Smart Chain RPC nodes for DeFi protocols. High throughput, low latency, EVM-compatible. Managed BSC infrastructure by Instanodes.",
    logoSrc: "/images/chains/bsc.svg",
    category: "EVM",
    hasPage: true,
    priority: 4,
  },
  {
    slug: "polygon-nodes",
    name: "Polygon",
    description: "High-throughput EVM-compatible L2. PoS sidechain with fast finality and low fees.",
    seoTitle: "Polygon Nodes | Polygon PoS RPC Endpoints | Instanodes",
    seoDescription:
      "Polygon PoS RPC nodes. High-throughput, EVM-compatible, low fees and fast finality. Dedicated Polygon infrastructure by Instanodes.",
    logoSrc: "/images/chains/polygon.png",
    category: "L2",
    hasPage: true,
    priority: 5,
  },
  {
    slug: "base-rpc-nodes",
    name: "Base",
    description: "Coinbase's OP Stack L2. Low-cost EVM-compatible rollup with fast finality.",
    seoTitle: "Base RPC Endpoints | Dedicated Base Nodes | Instanodes",
    seoDescription:
      "Dedicated Base RPC nodes for Coinbase's OP Stack L2. Low latency, fast finality, EVM-compatible. Managed Base infrastructure by Instanodes.",
    logoSrc: "/images/chains/base.png",
    category: "L2",
    hasPage: true,
    priority: 6,
  },
  {
    slug: "avalanche-rpc-node",
    name: "Avalanche",
    description: "Sub-second finality with customizable subnets. C-Chain and custom subnet support.",
    seoTitle: "Avalanche RPC Endpoints | Dedicated Avalanche Nodes | Instanodes",
    seoDescription:
      "Avalanche RPC nodes with sub-second finality. C-Chain and custom subnet support. Dedicated, managed infrastructure from Instanodes.",
    logoSrc: "/images/chains/avalanche.png",
    category: "L1",
    hasPage: true,
    priority: 7,
  },
  {
    slug: "polkadot-nodes",
    name: "Polkadot",
    description: "Shared security parachain network. Relay chain and parachain node support.",
    seoTitle: "Polkadot Nodes | Relay Chain and Parachain RPC Endpoints | Instanodes",
    seoDescription:
      "Polkadot relay chain and parachain nodes. Shared security, validator and collator support, managed by Instanodes.",
    logoSrc: "/images/chains/polkadot.png",
    category: "non-EVM",
    hasPage: true,
    priority: 8,
  },
  {
    slug: "ton-rpc-node",
    name: "TON",
    description: "Telegram's Open Network. High-speed blockchain with Lite and Full node options.",
    seoTitle: "TON RPC Endpoints | The Open Network Nodes | Instanodes",
    seoDescription:
      "TON (The Open Network) RPC nodes with Lite and Full node options. High-speed, dedicated infrastructure for Telegram-native dApps by Instanodes.",
    logoSrc: "/images/chains/ton.svg",
    category: "non-EVM",
    hasPage: true,
    priority: 9,
  },
  {
    slug: "tron-rpc-node",
    name: "Tron",
    description: "High-throughput blockchain powering USDT transactions and dApps.",
    seoTitle: "Tron RPC Endpoints | High-Throughput Tron Nodes | Instanodes",
    seoDescription:
      "Tron RPC nodes for USDT-heavy transactions and high-throughput dApps. Dedicated endpoints with reliable uptime from Instanodes.",
    logoSrc: "/images/chains/tron.svg",
    category: "non-EVM",
    hasPage: true,
    priority: 10,
  },
  {
    slug: "cosmos",
    name: "Cosmos",
    description: "The internet of blockchains. IBC-enabled nodes for the Cosmos ecosystem.",
    seoTitle: "Cosmos RPC Endpoints | Fast, Reliable Cosmos Nodes | Instanodes",
    seoDescription:
      "Fast, reliable Cosmos RPC nodes and endpoints. Full and archive nodes available. Deploy on Cosmos mainnet and testnet with Instanodes.",
    logoSrc: "/images/chains/cosmos.svg",
    category: "non-EVM",
    hasPage: true,
  },
  {
    slug: "fantom-rpc-node",
    name: "Fantom",
    description: "DAG-based aBFT consensus with EVM compatibility and fast finality.",
    seoTitle: "Fantom RPC Endpoints | aBFT EVM-Compatible Nodes | Instanodes",
    seoDescription:
      "Fantom RPC nodes with aBFT consensus, fast finality, and EVM compatibility. Dedicated, managed Fantom infrastructure by Instanodes.",
    logoSrc: "/images/chains/fantom.png",
    category: "EVM",
    hasPage: true,
  },
  {
    slug: "celo-nodes",
    name: "Celo",
    description: "Mobile-first EVM blockchain focused on financial inclusion.",
    seoTitle: "Celo Nodes | Mobile-First EVM Chain RPC Endpoints | Instanodes",
    seoDescription:
      "Dedicated Celo nodes for mobile-first EVM applications. Reliable RPC access, archive support, and managed infrastructure from Instanodes.",
    logoSrc: "/images/chains/celo.png",
    category: "EVM",
    hasPage: true,
  },
  {
    slug: "mantle-node",
    name: "Mantle",
    description: "Ethereum L2 with modular architecture and MNT token for gas.",
    seoTitle: "Mantle Nodes | Mantle L2 RPC Endpoints | Instanodes",
    seoDescription:
      "Mantle L2 RPC nodes with modular architecture support. Fast, dedicated endpoints with full and archive access from Instanodes.",
    logoSrc: "/images/chains/mantle.png",
    category: "L2",
    hasPage: true,
  },
  {
    slug: "tezos-rpc-node",
    name: "Tezos",
    description: "Self-amending blockchain with formal verification. Full baker and RPC node support.",
    seoTitle: "Tezos RPC Endpoints | Baker and Full Node Hosting | Instanodes",
    seoDescription:
      "Tezos RPC nodes and bakers. Self-amending blockchain with formal verification, full and archive support, managed by Instanodes.",
    logoSrc: "/images/chains/tezos.svg",
    category: "non-EVM",
    hasPage: true,
  },

  /* ── New 45 (catalog-only, no dedicated page) ────────────────────────────── */
  {
    slug: "xrp",
    name: "XRP Ledger",
    description: "Payment-focused L1 with 3-second finality. Settle high-volume transfers on XRP.",
    logoSrc: "/images/chains/xrp.svg",
    category: "non-EVM",
    hasPage: false,
  },
  {
    slug: "dogecoin",
    name: "Dogecoin",
    description: "Proof-of-work L1 with low fees. Send value across the Dogecoin UTXO chain.",
    logoSrc: "/images/chains/dogecoin.svg",
    category: "non-EVM",
    hasPage: false,
  },
  {
    slug: "cardano",
    name: "Cardano",
    description: "Peer-reviewed proof-of-stake L1. Build formally verified apps in Plutus.",
    logoSrc: "/images/chains/cardano.svg",
    category: "non-EVM",
    hasPage: false,
  },
  {
    slug: "arbitrum",
    name: "Arbitrum",
    description: "Optimistic rollup with the lowest-cost EVM L2 execution and Nitro sequencer.",
    logoSrc: "/images/chains/arbitrum.svg",
    category: "L2",
    hasPage: false,
  },
  {
    slug: "optimism",
    name: "Optimism",
    description: "Flagship OP Stack rollup. Run Ethereum apps with bedrock-era finality.",
    logoSrc: "/images/chains/optimism.svg",
    category: "L2",
    hasPage: false,
  },
  {
    slug: "litecoin",
    name: "Litecoin",
    description: "Bitcoin fork with 2.5-minute block times. Move value faster than BTC.",
    logoSrc: "/images/chains/litecoin.svg",
    category: "non-EVM",
    hasPage: false,
  },
  {
    slug: "stellar",
    name: "Stellar",
    description: "Cross-border payments network. Issue assets and settle FX through Stellar anchors.",
    logoSrc: "/images/chains/stellar.svg",
    category: "non-EVM",
    hasPage: false,
  },
  {
    slug: "near",
    name: "Near",
    description: "Sharded proof-of-stake L1. Run apps with low fees and parallel execution.",
    logoSrc: "/images/chains/near.svg",
    category: "L1",
    hasPage: false,
  },
  {
    slug: "aptos",
    name: "Aptos",
    description: "Move-language L1 from the former Diem team. Build parallel-execution apps.",
    logoSrc: "/images/chains/aptos.svg",
    category: "L1",
    hasPage: false,
  },
  {
    slug: "sui",
    name: "Sui",
    description: "Object-based Move L1. Develop horizontally scalable apps with predictable gas.",
    logoSrc: "/images/chains/sui.svg",
    category: "L1",
    hasPage: false,
  },
  {
    slug: "hedera",
    name: "Hedera",
    description: "Hashgraph-based DLT. Settle 10,000 TPS through enterprise governance.",
    logoSrc: "/images/chains/hedera.svg",
    category: "non-EVM",
    hasPage: false,
  },
  {
    slug: "internet-computer",
    name: "Internet Computer",
    description: "WebAssembly L1 hosting full web apps. Run end-to-end dApps on ICP canisters.",
    logoSrc: "/images/chains/internet-computer.svg",
    category: "non-EVM",
    hasPage: false,
  },
  {
    slug: "algorand",
    name: "Algorand",
    description: "Pure proof-of-stake L1 with instant finality. Build payment and CBDC apps.",
    logoSrc: "/images/chains/algorand.svg",
    category: "non-EVM",
    hasPage: false,
  },
  {
    slug: "cronos",
    name: "Cronos",
    description: "EVM-compatible Cosmos chain by Crypto.com. Deploy Solidity apps to Cronos.",
    logoSrc: "/images/chains/cronos.svg",
    category: "EVM",
    hasPage: false,
  },
  {
    slug: "vechain",
    name: "VeChain",
    description: "Supply chain blockchain. Track real-world assets through a dual-token model.",
    logoSrc: "/images/chains/vechain.svg",
    category: "non-EVM",
    hasPage: false,
  },
  {
    slug: "filecoin",
    name: "Filecoin",
    description: "Decentralized storage network. Store data through a market of providers.",
    logoSrc: "/images/chains/filecoin.svg",
    category: "non-EVM",
    hasPage: false,
  },
  {
    slug: "stacks",
    name: "Stacks",
    description: "Bitcoin smart contract layer. Build Bitcoin-secured apps in Clarity.",
    logoSrc: "/images/chains/stacks.svg",
    category: "non-EVM",
    hasPage: false,
  },
  {
    slug: "render",
    name: "Render",
    description: "GPU rendering network. Distribute graphics workloads across decentralized compute.",
    logoSrc: "",
    category: "non-EVM",
    hasPage: false,
  },
  {
    slug: "theta",
    name: "Theta",
    description: "Decentralized video streaming network. Power video delivery for your dApp.",
    logoSrc: "/images/chains/theta.svg",
    category: "non-EVM",
    hasPage: false,
  },
  {
    slug: "flow",
    name: "Flow",
    description: "Resource-oriented L1 for NFTs and games. Build consumer apps in Cadence.",
    logoSrc: "/images/chains/flow.svg",
    category: "non-EVM",
    hasPage: false,
  },
  {
    slug: "linea",
    name: "Linea",
    description: "ConsenSys zkEVM rollup. Build EVM apps with full Ethereum security.",
    logoSrc: "/images/chains/linea.svg",
    category: "L2",
    hasPage: false,
  },
  {
    slug: "zksync-era",
    name: "zkSync Era",
    description: "Type 1 zkEVM L2 from Matter Labs. Scale Ethereum apps with proof-based finality.",
    logoSrc: "/images/chains/zksync-era.svg",
    category: "L2",
    hasPage: false,
  },
  {
    slug: "scroll",
    name: "Scroll",
    description: "zkEVM rollup with bit-for-bit Ethereum compatibility. Native EVM equivalence.",
    logoSrc: "/images/chains/scroll.svg",
    category: "L2",
    hasPage: false,
  },
  {
    slug: "starknet",
    name: "Starknet",
    description: "Cairo-based ZK rollup. Run STARK-proven apps with provable execution.",
    logoSrc: "/images/chains/starknet.svg",
    category: "L2",
    hasPage: false,
  },
  {
    slug: "polygon-zkevm",
    name: "Polygon zkEVM",
    description: "Polygon's zero-knowledge rollup. EVM compatibility plus ZK security for your apps.",
    logoSrc: "/images/chains/polygon-zkevm.svg",
    category: "L2",
    hasPage: false,
  },
  {
    slug: "blast",
    name: "Blast",
    description: "Native yield L2. Earn yield on ETH and stables held in rollup wallets.",
    logoSrc: "/images/chains/blast.svg",
    category: "L2",
    hasPage: false,
  },
  {
    slug: "mode",
    name: "Mode",
    description: "OP Stack L2 with developer revenue sharing. Build apps that pay you back.",
    logoSrc: "/images/chains/mode.svg",
    category: "L2",
    hasPage: false,
  },
  {
    slug: "gnosis",
    name: "Gnosis",
    description: "EVM chain with stablecoin-native gas. Run apps at predictable transaction costs.",
    logoSrc: "/images/chains/gnosis.svg",
    category: "EVM",
    hasPage: false,
  },
  {
    slug: "moonbeam",
    name: "Moonbeam",
    description: "EVM parachain on Polkadot. Deploy Solidity apps with cross-chain XCM access.",
    logoSrc: "/images/chains/moonbeam.svg",
    category: "EVM",
    hasPage: false,
  },
  {
    slug: "kava",
    name: "Kava",
    description: "Cosmos chain with EVM co-chain. Build DeFi apps across IBC and EVM.",
    logoSrc: "/images/chains/kava.svg",
    category: "EVM",
    hasPage: false,
  },
  {
    slug: "klaytn",
    name: "Klaytn",
    description: "EVM L1 from Kakao. Build consumer apps on an enterprise-backed stack.",
    logoSrc: "/images/chains/klaytn.svg",
    category: "EVM",
    hasPage: false,
  },
  {
    slug: "conflux",
    name: "Conflux",
    description: "Tree-graph PoW/PoS hybrid. Run apps on a high-TPS architecture.",
    logoSrc: "/images/chains/conflux.svg",
    category: "EVM",
    hasPage: false,
  },
  {
    slug: "harmony",
    name: "Harmony",
    description: "Sharded EVM L1. Build apps with 2-second finality and cross-shard transfers.",
    logoSrc: "/images/chains/harmony.svg",
    category: "EVM",
    hasPage: false,
  },
  {
    slug: "aurora",
    name: "Aurora",
    description: "NEAR-based EVM. Deploy Solidity apps with NEAR finality and gas in ETH.",
    logoSrc: "/images/chains/aurora.svg",
    category: "EVM",
    hasPage: false,
  },
  {
    slug: "boba",
    name: "Boba",
    description: "OP Stack L2 with fast bridging. Deploy Ethereum apps at lower gas costs.",
    logoSrc: "/images/chains/boba.svg",
    category: "L2",
    hasPage: false,
  },
  {
    slug: "metis",
    name: "Metis",
    description: "OP Stack L2 with a decentralized sequencer. Build DAOs and dApps at scale.",
    logoSrc: "/images/chains/metis.svg",
    category: "L2",
    hasPage: false,
  },
  {
    slug: "manta",
    name: "Manta",
    description: "Modular L2 with ZK privacy. Add private compute to your dApp.",
    logoSrc: "/images/chains/manta.svg",
    category: "L2",
    hasPage: false,
  },
  {
    slug: "berachain",
    name: "Berachain",
    description: "Proof-of-liquidity EVM L1. Build apps where LPs secure the chain.",
    logoSrc: "/images/chains/berachain.svg",
    category: "EVM",
    hasPage: false,
  },
  {
    slug: "sei",
    name: "Sei",
    description: "Order-book-optimized L1 from Cosmos. Run on-chain orderbook apps.",
    logoSrc: "/images/chains/sei.svg",
    category: "non-EVM",
    hasPage: false,
  },
  {
    slug: "injective",
    name: "Injective",
    description: "Order-book L1 with EVM and Cosmos compatibility. Build derivatives apps.",
    logoSrc: "/images/chains/injective.svg",
    category: "EVM",
    hasPage: false,
  },
  {
    slug: "osmosis",
    name: "Osmosis",
    description: "Cosmos DEX hub. Run AMM apps on an IBC-native chain.",
    logoSrc: "/images/chains/osmosis.svg",
    category: "non-EVM",
    hasPage: false,
  },
  {
    slug: "kaspa",
    name: "Kaspa",
    description: "BlockDAG L1. Mine and transact at 1-second block times.",
    logoSrc: "/images/chains/kaspa.svg",
    category: "non-EVM",
    hasPage: false,
  },
  {
    slug: "bittensor",
    name: "Bittensor",
    description: "Decentralized AI compute network. Train models across a subnet architecture.",
    logoSrc: "/images/chains/bittensor.svg",
    category: "non-EVM",
    hasPage: false,
  },
  {
    slug: "pulsechain",
    name: "PulseChain",
    description: "Ethereum fork with native gas token PLS. Migrate EVM apps in minutes.",
    logoSrc: "/images/chains/pulsechain.svg",
    category: "EVM",
    hasPage: false,
  },
  {
    slug: "monad",
    name: "Monad",
    description: "Parallel EVM L1 targeting 10,000 TPS. Scale Ethereum apps without rewriting code.",
    logoSrc: "/images/chains/monad.svg",
    category: "EVM",
    hasPage: false,
  },
];
